<template>
  <div class="hertz-experiment-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$c = λ · f$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <defs>
            <filter id="glow-spark" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            
            <marker id="arrow-em" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
              <polygon points="0 0, 6 2, 0 4" fill="#06b6d4" />
            </marker>
          </defs>

          <!-- Transmitter (Dipole Oscillator) -->
          <g transform="translate(60, 110)">
            <rect x="-30" y="-10" width="60" height="20" rx="4" fill="#1e293b" stroke="#334155" />
            <rect x="-10" y="-30" width="20" height="60" rx="2" fill="#0f172a" stroke="#475569" />
            
            <!-- Electrodes -->
            <line x1="-30" y1="0" x2="-4" y2="0" stroke="#94a3b8" stroke-width="4" stroke-linecap="round" />
            <line x1="30" y1="0" x2="4" y2="0" stroke="#94a3b8" stroke-width="4" stroke-linecap="round" />
            <circle cx="-30" cy="0" r="10" fill="#334155" stroke="#64748b" stroke-width="2" />
            
            <circle cx="30" cy="0" r="10" fill="#334155" stroke="#64748b" stroke-width="2" />
            
            <!-- Spark Generator -->
            <g v-if="sparking && sparkVisible">
              <path d="M -4,0 L 0,-6 L 4,0 L 0,6 Z" fill="#22d3ee" filter="url(#glow-spark)" />
            </g>
            <text x="0" y="55" fill="#64748b" font-weight="700" font-size="10" text-anchor="middle">发射器 (感应圈)</text>
          </g>

          <!-- EM Waves Radiation Pattern -->
          <g v-if="sparking">
             <g v-for="(wave, i) in emWaves" :key="i">
                <!-- Positive amplitude E-field wave fronts (solid) -->
                <path :d="`M ${ 60 + wave.r }, 110 Q ${ 60 + wave.r }, 60  ${ 60 + wave.r * 0.8 }, 30`" fill="none" stroke="#0ea5e9" stroke-width="1.5" :opacity="wave.opacity" />
                <path :d="`M ${ 60 + wave.r }, 110 Q ${ 60 + wave.r }, 160 ${ 60 + wave.r * 0.8 }, 190`" fill="none" stroke="#0ea5e9" stroke-width="1.5" :opacity="wave.opacity" />
             </g>
          </g>

          <line x1="120" y1="110" x2="280" y2="110" stroke="#475569" stroke-width="1" stroke-dasharray="4,4" />

          <!-- Receiver (Resonator Loop) -->
          <g transform="translate(320, 110)">
            <circle r="40" fill="none" stroke="#475569" stroke-width="4" stroke-dasharray="245, 12" transform="rotate(-90)" />
            <circle cx="-40" cy="0" r="4" fill="#94a3b8" />
            <circle cx="-40" cy="4" r="2" fill="#fff" />
            
            <!-- Induced Spark -->
            <g v-if="sparking && sparkVisible">
              <circle cx="0" cy="-40" r="4" fill="#22d3ee" filter="url(#glow-spark)">
                 <animate attributeName="opacity" values="1;0;1" dur="0.05s" repeatCount="indefinite" />
              </circle>
            </g>
            <text x="0" y="65" fill="#64748b" font-weight="700" font-size="10" text-anchor="middle">接收器 (共振环)</text>
          </g>

          <!-- Propagation vector -->
          <line x1="150" y1="80" x2="210" y2="80" stroke="#06b6d4" stroke-width="2" marker-end="url(#arrow-em)" opacity="0.6" />
          <text x="160" y="70" fill="#06b6d4" font-size="10" opacity="0.8">波速 c (横波)</text>

        </svg>

        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>实验系统状态</label>
            <span class="val" :style="{color: sparking ? '#22c55e' : '#f8fafc'}">{{ sparking ? '高频电磁振荡中' : '静默' }}</span>
          </div>
          <div class="hud-item orange">
            <label>激发频率模拟参数 f</label>
            <span class="val">{{ (computedFreq * 100).toFixed(0) }} <small>MHz</small></span>
          </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
          <div class="control-vertical-group">
            <div class="param-row flex flex-row items-center gap-4">
              <div class="flex-1">
                <el-button :type="sparking ? 'danger' : 'primary'" size="small" @click="toggleSpark" class="w-full" icon="el-icon-lightning">
                  {{ sparking ? '切断感应电圈' : '开启高频高压放电' }}
                </el-button>
              </div>
              <div class="flex-1">
                 <label style="font-size:11px;color:#64748b;font-weight:700;">调节振荡频率</label>
                 <el-slider v-model="freqRaw" :min="10" :max="100" size="small"></el-slider>
              </div>
            </div>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-info"></i>
          <p><strong>深入剖析：</strong>1887年，海因里希·赫兹利用感应圈引发偶极子天线的高频火花放电。这不仅是一次放电，根据麦克斯韦方程组，加速电荷会向外强烈辐射<strong>不断交变的电场和磁场</strong>（电磁波）。即使没有导线连接，电磁波以光速穿过空间，激发了远处的次级开口谐振环，使其间隙产生感应火花！这一硬核实验证实了麦氏“电磁波存在”的惊天预言，直接敲开了近代无线电通讯与雷达技术的大门。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HertzExperimentViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      sparking: false,
      sparkVisible: false,
      freqRaw: 50,
      emWaves: [],
      timer: null,
      sparkTimer: null
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    computedFreq() {
      // mapped frequency parameter
      return this.freqRaw / 10;
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/频率|快/.test(kp)) this.freqRaw = 80;
      else if (/长波|慢/.test(kp)) this.freqRaw = 20;
    },
    toggleSpark() {
      this.sparking = !this.sparking;
      if (this.sparking) {
        this.emWaves = [];
        this.waveEmmiter();
      } else {
        cancelAnimationFrame(this.timer);
        clearInterval(this.sparkTimer);
      }
    },
    waveEmmiter() {
      if (!this.sparking) return;
      
      this.emWaves.push({ r: 0, opacity: 1 });
      
      const animateWave = () => {
         if(!this.sparking) return;
         for (let i = this.emWaves.length - 1; i >= 0; i--) {
            this.emWaves[i].r += (this.computedFreq * 0.8 + 2); // propagation speed + freq effect
            this.emWaves[i].opacity -= 0.015;
            if (this.emWaves[i].opacity <= 0) {
               this.emWaves.splice(i, 1);
            }
         }
         this.timer = requestAnimationFrame(animateWave);
      };
      
      animateWave();
      
      // Emit next wave based on frequency
      this.sparkTimer = setInterval(() => {
         if(this.sparking) {
            this.emWaves.push({ r: 0, opacity: 1 });
            this.sparkVisible = !this.sparkVisible; // Blink sparks rapidly
         }
      }, 1000 / this.computedFreq);
    }
  },
  beforeDestroy() {
    this.sparking = false;
    cancelAnimationFrame(this.timer);
    clearInterval(this.sparkTimer);
  }
}
</script>

<style scoped lang="scss">
.hertz-experiment-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; } .w-full { width: 100%; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
